import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../components/ClassicToast";
import {
  Users,
  UserPlus,
  Search,
  Filter,
  Trash2,
  X,
  Upload,
  Mail,
  Phone,
  MapPin,
  Loader,
  GraduationCap,
  FileText,
  User,
  Eye,
  Link2,
  BookOpen,
  Check,
  Receipt,
  Pencil,
} from "lucide-react";
import {
  lmsGetStudents,
  lmsCreateStudent,
  lmsUpdateStudent,
  lmsDeleteStudent,
  lmsGetSchoolClasses,
  lmsGetClasses,
  lmsGetCoursesByClassAndStudentId,
  lmsCreateEnrollments,
} from "../api/lmsApi";
import { BASE_URL } from "../../../configs/WebServiceUrl";
import { checkStudentCreationLimit } from "../../../utils/studentLimitUtils";
import "../lms.css";

const getSchoolData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const adminId = ud.admin_id || ud.school_id || ud.id || null;
    return { admin_id: adminId, ...ud };
  } catch {
    return { admin_id: null };
  }
};

export default function Students() {
  const navigate = useNavigate();
  const reduxUser = useSelector((state) => state.authSlice?.user);
  const school = getSchoolData();
  const schoolId = school.admin_id;
  const createdBy = school.admin_id;
  const currentPayments = reduxUser?.payments || school?.payments || [];
  const [userPayments, setUserPayments] = useState(
    Array.isArray(currentPayments) && currentPayments.length > 0 ? currentPayments : []
  );

  // State
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClassFilter, setSelectedClassFilter] = useState("");

  // Pagination State
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Student Courses Modal State (Checkbox Selection & Fee Paid)
  const [coursesModalOpen, setCoursesModalOpen] = useState(false);
  const [modalStudent, setModalStudent] = useState(null);
  const [studentCourses, setStudentCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [paymentType, setPaymentType] = useState("cash");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [remarks, setRemarks] = useState("");

  const handleOpenCoursesModal = async (student) => {
    setModalStudent(student);
    setCoursesModalOpen(true);
    setLoadingCourses(true);
    setStudentCourses([]);

    const studentKey = student.id || student.student_id || student.admission_number || "default";
    const studentId = student.id || student.student_id;

    try {
      const classId = student.class_id;
      if (classId && studentId) {
        const res = await lmsGetCoursesByClassAndStudentId(classId, studentId, schoolId);
        const data = res.data?.data || res.data || [];
        const courseList = Array.isArray(data) ? data : [];
        setStudentCourses(courseList);

        // Do not auto-select courses by default for any student
        setSelectedCourseIds([]);

        setPaymentType("cash");
        setScreenshotFile(null);
        setScreenshotPreview(null);
        setRemarks("");
      } else {
        setStudentCourses([]);
        setSelectedCourseIds([]);
      }
    } catch (error) {
      console.error("Error fetching courses for student class:", error);
      showToast.error("Failed to load courses for student");
    } finally {
      setLoadingCourses(false);
    }
  };

  // Helper to determine effective price (discount_price preferred over price)
  const getCoursePrice = (c) => {
    if (!c || c.is_free) return 0;
    return parseFloat(c.discount_price ?? c.price ?? 0) || 0;
  };

  // Helper to check if a course is already fully paid
  const isCourseFullyPaid = (c) => {
    if (!c) return false;
    if (c.is_free) return true;
    if (c.payment_status === "paid") return true;
    const price = getCoursePrice(c);
    const paid = parseFloat(c.total_paid_amount) || 0;
    return price > 0 && paid >= price;
  };

  // Helper to calculate payable (unpaid remaining) amount for a course
  const getCoursePayableAmount = (c) => {
    if (!c || isCourseFullyPaid(c)) return 0;
    const price = getCoursePrice(c);
    const paid = parseFloat(c.total_paid_amount) || 0;
    return Math.max(0, price - paid);
  };

  const handleToggleCourseSelect = (courseId) => {
    const course = studentCourses.find((c) => c.id === courseId);
    if (course && isCourseFullyPaid(course)) return;

    setSelectedCourseIds((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handleToggleSelectAll = () => {
    const selectableCourses = studentCourses.filter((c) => !isCourseFullyPaid(c));
    const nonPaidSelected = selectedCourseIds.filter((id) => {
      const crs = studentCourses.find((c) => c.id === id);
      return crs && !isCourseFullyPaid(crs);
    });

    if (nonPaidSelected.length === selectableCourses.length) {
      // Unselect non-paid courses, keep paid ones
      setSelectedCourseIds(
        studentCourses.filter((c) => isCourseFullyPaid(c)).map((c) => c.id)
      );
    } else {
      setSelectedCourseIds(studentCourses.map((c) => c.id));
    }
  };

  const handleSaveStudentCourses = async () => {
    if (!modalStudent) return;
    if (selectedCourseIds.length === 0) {
      showToast.error("Please select at least one course to assign");
      return;
    }

    let remainingPool = totalSelectedAmount;
    const pMethod = paymentType === "online" ? "upi_qr" : paymentType === "bank_transfer" ? "bank_transfer" : "cash";

    try {
      const payloadItems = selectedCourseIds.map((cId) => {
        const crs = studentCourses.find((c) => c.id === cId) || {};
        const fullyPaid = isCourseFullyPaid(crs);
        const targetPrice = getCoursePrice(crs);
        const prevPaid = parseFloat(crs.total_paid_amount) || 0;
        const needed = Math.max(0, targetPrice - prevPaid);

        let allocAmount = 0;
        if (!fullyPaid && !crs.is_free && targetPrice > 0 && remainingPool > 0 && needed > 0) {
          allocAmount = Math.min(remainingPool, needed);
          remainingPool -= allocAmount;
        }

        return {
          enrollment_id: crs.enrollment_id || null,
          school_id: schoolId,
          student_id: modalStudent.id || modalStudent.student_id,
          course_id: cId,
          enrolled_by: createdBy || schoolId,
          fee_paid: "yes",
          amount: allocAmount > 0 ? allocAmount : targetPrice,
          payment_method: pMethod,
          payment_type: paymentType || "cash",
          remarks: remarks.trim() || `Enrolled in course ${crs.name || cId}`,
        };
      });

      if (screenshotFile) {
        const formData = new FormData();
        formData.append("student_id", modalStudent.id || modalStudent.student_id);
        formData.append("school_id", schoolId);
        formData.append("screenshot", screenshotFile);
        formData.append("enrollments", JSON.stringify(payloadItems));
        await lmsCreateEnrollments(formData);
      } else {
        await lmsCreateEnrollments(payloadItems);
      }

      showToast.success(`Assigned ${selectedCourseIds.length} course(s) successfully!`);
      handleCloseCoursesModal();
    } catch (e) {
      console.error("Error saving course enrollments:", e);
      showToast.error(e.response?.data?.message || "Failed to save course assignments");
    }
  };

  const handleCloseCoursesModal = () => {
    setCoursesModalOpen(false);
    setModalStudent(null);
    setStudentCourses([]);
    setSelectedCourseIds([]);
    setPaymentType("cash");
    setScreenshotFile(null);
    setScreenshotPreview(null);
    setRemarks("");
  };

  // Total Amount Calculation for Selected Courses (excluding already paid courses)
  const totalSelectedAmount = studentCourses
    .filter((c) => selectedCourseIds.includes(c.id))
    .reduce((sum, c) => sum + getCoursePayableAmount(c), 0);

  // Form State
  const [formData, setFormData] = useState({
    admission_number: "",
    class_id: "",
    class_name: "",
    first_name: "",
    last_name: "",
    gender: "male",
    email: "",
    mobile: "",
    address: "",
    profile_image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [editingStudentId, setEditingStudentId] = useState(null);

  // Load Payments on mount
  useEffect(() => {
    const loadPayments = async () => {
      if (!schoolId) return;
      try {
        const res = await fetch(`${BASE_URL}/api/profile/school-user-details/${schoolId}`);
        const data = await res.json();
        const u = data.user || data;
        if (Array.isArray(u?.payments) && u.payments.length > 0) {
          setUserPayments(u.payments);
        }
      } catch (e) {
        console.error("Error loading user payments:", e);
      }
    };
    loadPayments();
  }, [schoolId]);

  // Load Classes on mount
  useEffect(() => {
    fetchClasses();
  }, [schoolId]);

  // Fetch Students on schoolId, selectedClassFilter, page, or searchQuery change
  useEffect(() => {
    fetchStudents();
  }, [schoolId, selectedClassFilter, page, searchQuery]);

  // Handle Class Filter Change (resets page to 1)
  const handleClassFilterChange = (e) => {
    setSelectedClassFilter(e.target.value);
    setPage(1);
  };

  // Fetch Classes for Dropdown by School ID
  const fetchClasses = async () => {
    try {
      let fetchedClasses = [];
      if (schoolId) {
        const res = await lmsGetSchoolClasses(schoolId);
        const data = res.data?.data || res.data || [];
        if (Array.isArray(data) && data.length > 0) {
          fetchedClasses = data;
        }
      }

      if (fetchedClasses.length === 0) {
        const fallbackRes = await lmsGetClasses();
        fetchedClasses = fallbackRes.data?.data || fallbackRes.data || [];
      }

      setClasses(fetchedClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  // Fetch Students
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {
        school_id: schoolId,
        page,
        limit,
      };
      if (selectedClassFilter) {
        params.class_id = selectedClassFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const res = await lmsGetStudents(params);
      const resData = res.data;
      const studentList = resData?.data || (Array.isArray(resData) ? resData : []);
      const total = resData?.total ?? studentList.length;
      const pages = resData?.totalPages ?? (Math.ceil(total / limit) || 1);

      setStudents(studentList);
      setTotalCount(total);
      setTotalPages(pages);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Form Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "class_id") {
      const selectedCls = classes.find(
        (c) => String(c.class_id) === String(value)
      );
      setFormData((prev) => ({
        ...prev,
        class_id: value,
        class_name: selectedCls
          ? selectedCls.name || selectedCls.class_name || selectedCls.title
          : "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle Image File Selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, profile_image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Open Modal (Triggered by Create Student button)
  const handleOpenModal = () => {
    const currentCount = Math.max(totalCount || 0, students?.length || 0);
    const activePayments = userPayments.length > 0 ? userPayments : currentPayments;
    const { isExceeded, limit } = checkStudentCreationLimit(currentCount, activePayments);

    if (isExceeded) {
      showToast.error(
        `You have exceeded your plan limit (${limit} students). Upgrade your plan to create students.`
      );
      return;
    }

    setFormData({
      admission_number: "",
      class_id: "",
      class_name: "",
      first_name: "",
      last_name: "",
      gender: "male",
      email: "",
      mobile: "",
      address: "",
      profile_image: null,
    });
    setImagePreview(null);
    setEditingStudentId(null);
    setIsModalOpen(true);
  };

  // Open Modal in Edit mode (prefilled with student data)
  const handleOpenEditModal = (st) => {
    const matchedCls = classes.find(
      (c) => String(c.class_id) === String(st.class_id)
    );
    setFormData({
      admission_number: st.admission_number || "",
      class_id: st.class_id ? String(st.class_id) : "",
      class_name:
        st.class_name ||
        (matchedCls ? matchedCls.name || matchedCls.class_name || matchedCls.title : ""),
      first_name: st.first_name || "",
      last_name: st.last_name || "",
      gender: st.gender || "male",
      email: st.email || "",
      mobile: st.mobile || st.contact_number || "",
      address: st.address || "",
      profile_image: null,
    });
    const img = typeof st.profile_image === "string" ? st.profile_image : st.profile_image_url;
    setImagePreview(img || null);
    setEditingStudentId(st.id || st.student_id);
    setIsModalOpen(true);
  };

  // Close Modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setImagePreview(null);
    setEditingStudentId(null);
  };

  // Handle Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.admission_number ||
      !formData.first_name ||
      !formData.last_name ||
      !formData.email ||
      !formData.mobile
    ) {
      showToast.error("Please fill in all required fields!");
      return;
    }

    setSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("admission_number", formData.admission_number);
      payload.append("class_id", formData.class_id);
      payload.append("class_name", formData.class_name);
      payload.append("first_name", formData.first_name);
      payload.append("last_name", formData.last_name);
      payload.append("gender", formData.gender || "male");
      payload.append("email", formData.email);
      payload.append("mobile", formData.mobile);
      payload.append("address", formData.address);
      
      if (schoolId) {
        payload.append("school_id", schoolId);
      }
      if (createdBy) {
        payload.append("created_by", createdBy);
      }
      
      if (formData.profile_image) {
        payload.append("profile_image", formData.profile_image);
      }

      if (editingStudentId) {
        const res = await lmsUpdateStudent(editingStudentId, payload);
        const updated = res.data?.data;
        setStudents((prev) =>
          prev.map((s) =>
            (s.id || s.student_id) === editingStudentId
              ? updated || { ...s, ...formData, profile_image: imagePreview }
              : s
          )
        );
        showToast.success("Student updated successfully!");
        handleCloseModal();
        return;
      }

      const res = await lmsCreateStudent(payload);
      const newStudent = res.data?.data || res.data || {
        id: Date.now(),
        ...formData,
        profile_image_url: imagePreview,
      };

      setStudents((prev) => [newStudent, ...prev]);
      showToast.success("Student created successfully!");
      handleCloseModal();
    } catch (error) {
      console.error(editingStudentId ? "Error updating student:" : "Error creating student:", error);
      showToast.error(
        error.response?.data?.message ||
          (editingStudentId ? "Failed to update student" : "Failed to create student")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete Student
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this student?"))
      return;
    try {
      await lmsDeleteStudent(id);
      showToast.success("Student deleted successfully!");
    } catch (error) {
      showToast.success("Student removed!");
    } finally {
      setStudents((prev) => prev.filter((s) => s.id !== id));
    }
  };

  // Filter students
  const filteredStudents = students.filter((s) => {
    const fullName = `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase();
    const admNo = String(s.admission_number || "").toLowerCase();
    const email = (s.email || "").toLowerCase();
    const query = searchQuery.toLowerCase();

    const matchesSearch =
      fullName.includes(query) || admNo.includes(query) || email.includes(query);

    const matchesClass = selectedClassFilter
      ? String(s.class_id || s.class_name) === String(selectedClassFilter)
      : true;

    return matchesSearch && matchesClass;
  });

  return (
    <div className="std-compact-container">
      <style>{`
        .std-compact-container {
          padding: 14px 18px !important;
          max-width: 1400px;
          margin: 0 auto;
        }
        .std-compact-header {
          margin-bottom: 12px !important;
        }
        .std-compact-header .std-page-title {
          font-size: 20px !important;
          margin-bottom: 2px !important;
        }
        .std-compact-header .std-page-subtitle {
          font-size: 12.5px !important;
        }
        .std-compact-stats {
          margin-bottom: 12px !important;
          gap: 12px !important;
        }
        .std-compact-stats .std-stat-card {
          padding: 10px 14px !important;
        }
        .std-compact-stats .std-stat-icon-wrap {
          width: 36px !important;
          height: 36px !important;
        }
        .std-compact-stats .std-stat-label {
          font-size: 11.5px !important;
        }
        .std-compact-stats .std-stat-value {
          font-size: 16px !important;
        }
        .std-compact-toolbar {
          margin-bottom: 12px !important;
          padding: 8px 12px !important;
        }
        .std-table-compact th {
          padding: 7px 10px !important;
          font-size: 11px !important;
          background: #f8fafc !important;
          white-space: nowrap !important;
          position: sticky;
          top: 0;
          z-index: 2;
        }
        .std-table-compact td {
          padding: 5px 10px !important;
          font-size: 12px !important;
          vertical-align: middle !important;
        }
        .std-table-compact .std-avatar, 
        .std-table-compact .std-avatar-placeholder {
          width: 28px !important;
          height: 28px !important;
          font-size: 12px !important;
        }
        .std-table-scroll-container {
          width: 100%;
          overflow-x: auto;
          overflow-y: auto;
          max-height: calc(100vh - 240px);
          -webkit-overflow-scrolling: touch;
        }
        @media (max-width: 768px) {
          .std-compact-container {
            padding: 8px 10px !important;
          }
          .std-table-scroll-container {
            max-height: calc(100vh - 270px);
          }
        }
      `}</style>

      {/* Header */}
      <div className="std-page-header std-compact-header">
        <div>
          <h1 className="std-page-title">Student Directory</h1>
          <p className="std-page-subtitle">
            Manage student enrollments, basic info, and class assignments
          </p>
        </div>
        <button className="std-btn-primary" onClick={handleOpenModal} style={{ padding: "6px 14px", fontSize: "13px" }}>
          <UserPlus size={16} />
          Create Student
        </button>
      </div>

      {/* Stats Cards */}
      <div className="std-stats-grid std-compact-stats">
        <div className="std-stat-card">
          <div
            className="std-stat-icon-wrap"
            style={{ background: "#eff6ff", color: "#1c498d" }}
          >
            <Users size={18} />
          </div>
          <div>
            <div className="std-stat-label">Total Enrolled</div>
            <div className="std-stat-value">{totalCount}</div>
          </div>
        </div>

        <div className="std-stat-card">
          <div
            className="std-stat-icon-wrap"
            style={{ background: "#f0fdf4", color: "#166534" }}
          >
            <GraduationCap size={18} />
          </div>
          <div>
            <div className="std-stat-label">Classes Configured</div>
            <div className="std-stat-value">{classes.length}</div>
          </div>
        </div>

        <div className="std-stat-card">
          <div
            className="std-stat-icon-wrap"
            style={{ background: "#fffbebe1", color: "#b45309" }}
          >
            <FileText size={18} />
          </div>
          <div>
            <div className="std-stat-label">Academic Status</div>
            <div className="std-stat-value" style={{ fontSize: "15px" }}>
              Active Session
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="std-toolbar-card std-compact-toolbar">
        <div className="std-search-box">
          <Search size={15} className="std-search-icon" />
          <input
            type="text"
            className="std-search-input"
            placeholder="Search by student name, admission number, or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            style={{ padding: "6px 10px 6px 34px", fontSize: "13px" }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <select
            className="std-filter-select"
            value={selectedClassFilter}
            onChange={handleClassFilterChange}
            style={{ padding: "5px 10px", fontSize: "12.5px" }}
          >
            <option value="">All Classes</option>
            {classes.map((cls) => {
              const val = cls.class_id || cls.name;
              const label =
                cls.name || cls.class_name || cls.title || `Class ${cls.id}`;
              return (
                <option key={val} value={val}>
                  {label}
                </option>
              );
            })}
          </select>
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "500", whiteSpace: "nowrap" }}>
            Showing {students.length} of {totalCount} Records
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="std-table-card">
        <div className="std-table-scroll-container">
          <table className="std-table std-table-compact" style={{ minWidth: "900px" }}>
            <thead>
              <tr>
                <th>Student Details</th>
                <th>Admission No</th>
                <th>Class</th>
                <th>Contact Email</th>
                <th>Mobile</th>
                <th>Address</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "30px" }}>
                    <Loader className="spinner" size={22} style={{ margin: "0 auto 6px" }} />
                    <div style={{ color: "#64748b", fontSize: "13px" }}>Loading students...</div>
                  </td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "36px 20px" }}>
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        background: "#f1f5f9",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 8px",
                      }}
                    >
                      <User size={22} />
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "#334155" }}>
                      No student records found
                    </div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                      Click "Create Student" above to add your first student.
                    </div>
                  </td>
                </tr>
              ) : (
                students.map((st) => {
                  const fName = st.first_name || "";
                  const lName = st.last_name || "";
                  const profileImg = typeof st.profile_image === "string" ? st.profile_image : st.profile_image_url || st.url;
                  return (
                    <tr key={st.id || st.admission_number}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {profileImg ? (
                            <img
                              src={profileImg}
                              alt="Student"
                              className="std-avatar"
                            />
                          ) : (
                            <div className="std-avatar-placeholder">
                              {(fName[0] || "S").toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: "600", color: "#0f172a", fontSize: "12.5px" }}>
                              {fName} {lName}
                            </div>
                          </div>
                        </div>
                      </td>
                    <td>
                      <span className="std-badge-adm" style={{ fontSize: "11px", padding: "2px 6px" }}>
                        {st.admission_number || "N/A"}
                      </span>
                    </td>
                    <td>
                      <span className="std-badge-class" style={{ fontSize: "11px", padding: "2px 6px" }}>
                        {st.class_name ||
                          classes.find(
                            (c) => String(c.id) === String(st.class_id)
                          )?.name ||
                          st.class_id ||
                          "Class 1"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "5px", color: "#475569", fontSize: "12px" }}>
                        <Mail size={13} style={{ color: "#94a3b8" }} />
                        {st.email}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "5px", color: "#475569", fontSize: "12px" }}>
                        <Phone size={13} style={{ color: "#94a3b8" }} />
                        {st.mobile}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          color: "#64748b",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "12px",
                        }}
                        title={st.address}
                      >
                        <MapPin size={13} style={{ color: "#94a3b8", flexShrink: 0 }} />
                        {st.address || "N/A"}
                      </div>
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(st)}
                          title="Edit Student"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                            padding: "4px 10px",
                            borderRadius: "5px",
                            background: "#fffbeb",
                            color: "#b45309",
                            border: "1px solid #fde68a",
                            cursor: "pointer",
                            fontSize: "11.5px",
                            fontWeight: "600",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Pencil size={13} />
                          <span>Edit</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenCoursesModal(st)}
                          title="View Courses for Student Class"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                            padding: "4px 10px",
                            borderRadius: "5px",
                            background: "#eff6ff",
                            color: "#1c498d",
                            border: "1px solid #bfdbfe",
                            cursor: "pointer",
                            fontSize: "11.5px",
                            fontWeight: "600",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Link2 size={13} />
                          <span>Courses</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => navigate(`/lms/students/${st.id || st.student_id}/history/payment-details`)}
                          title="View Student Payment History & Course Requests"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                            padding: "4px 10px",
                            borderRadius: "5px",
                            background: "#f0fdf4",
                            color: "#166534",
                            border: "1px solid #bbf7d0",
                            cursor: "pointer",
                            fontSize: "11.5px",
                            fontWeight: "600",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Receipt size={13} />
                          <span>Payment History</span>
                        </button>
                      </div>
                    </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({totalCount} Total Students)
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                style={{
                  padding: "4px 12px",
                  borderRadius: "5px",
                  border: "1px solid #cbd5e1",
                  background: page <= 1 ? "#f1f5f9" : "#ffffff",
                  color: page <= 1 ? "#94a3b8" : "#334155",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: "12px",
                }}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                style={{
                  padding: "4px 12px",
                  borderRadius: "5px",
                  border: "1px solid #cbd5e1",
                  background: page >= totalPages ? "#f1f5f9" : "#ffffff",
                  color: page >= totalPages ? "#94a3b8" : "#334155",
                  cursor: page >= totalPages ? "not-allowed" : "pointer",
                  fontWeight: "500",
                  fontSize: "12px",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CLASSIC CREATE STUDENT POPUP MODAL */}
      {isModalOpen && (
        <div className="std-modal-overlay">
          <div className="std-modal-card">
            {/* Modal Header */}
            <div className="std-modal-header">
              <h3 className="std-modal-title">
                {editingStudentId ? <Pencil size={20} /> : <UserPlus size={20} />}
                {editingStudentId ? "Edit Student Record" : "Create New Student Record"}
              </h3>
              <button
                className="std-modal-close"
                onClick={handleCloseModal}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form Body */}
            <form onSubmit={handleSubmit}>
              <div className="std-modal-body">
                {/* Photo & Main Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "110px 1fr",
                    gap: "20px",
                    alignItems: "start",
                    marginBottom: "20px",
                  }}
                >
                  {/* Avatar Upload Preview */}
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        width: "90px",
                        height: "90px",
                        borderRadius: "50%",
                        border: "2px dashed #cbd5e1",
                        background: "#f8fafc",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 8px",
                        position: "relative",
                      }}
                    >
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div style={{ color: "#94a3b8", textAlign: "center" }}>
                          <User size={32} />
                        </div>
                      )}
                    </div>

                    <label
                      htmlFor="std_profile_img"
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#1c498d",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Upload size={12} /> Photo
                    </label>
                    <input
                      id="std_profile_img"
                      type="file"
                      name="profile_image"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleImageChange}
                    />
                  </div>

                  {/* Primary Fields */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "14px",
                    }}
                  >
                    {/* Admission Number */}
                    <div>
                      <label className="std-field-label">
                        Admission No <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="admission_number"
                        className="std-field-input"
                        placeholder="e.g. ADM-2026-001"
                        value={formData.admission_number}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Class Dropdown (by school_id) */}
                    <div>
                      <label className="std-field-label">
                        Class <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <select
                        name="class_id"
                        className="std-field-input"
                        value={formData.class_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Class...</option>
                        {classes.map((cls) => {
                          const val = cls.class_id || cls.name;
                          const label =
                            cls.name ||
                            cls.class_name ||
                            cls.title ||
                            `Class ${cls.class_id}`;
                          return (
                            <option key={val} value={val}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* First Name */}
                    <div>
                      <label className="std-field-label">
                        First Name <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        className="std-field-input"
                        placeholder="First Name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="std-field-label">
                        Last Name <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        className="std-field-input"
                        placeholder="Last Name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "14px",
                    marginBottom: "14px",
                  }}
                >
                  {/* Email */}
                  <div>
                    <label className="std-field-label">
                      Email Address <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="std-field-input"
                      placeholder="student@school.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="std-field-label">
                      Mobile Number <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      className="std-field-input"
                      placeholder="Mobile / Phone Number"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="std-field-label">Residential Address</label>
                  <textarea
                    name="address"
                    className="std-field-input"
                    rows="2"
                    placeholder="Enter student residential address..."
                    value={formData.address}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="std-modal-footer">
                <button
                  type="button"
                  className="std-btn-cancel"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="std-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader size={16} className="spinner" />
                      Saving...
                    </>
                  ) : editingStudentId ? (
                    "Update Student"
                  ) : (
                    "Create Student"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* STUDENT CLASS COURSES POPUP MODAL */}
      {coursesModalOpen && modalStudent && (
        <div className="std-modal-overlay" onClick={handleCloseCoursesModal}>
          <div
            className="std-modal-card"
            style={{ maxWidth: "760px", width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="std-modal-header" style={{ background: "#1c498d", color: "#ffffff" }}>
              <div>
                <h3 className="std-modal-title" style={{ color: "#ffffff", gap: "10px" }}>
                  <BookOpen size={20} />
                  Courses for {modalStudent.first_name} {modalStudent.last_name}
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px", opacity: 0.9 }}>
                  Class: <strong>{modalStudent.class_name || modalStudent.class_id || "N/A"}</strong> | Admission No: <strong>{modalStudent.admission_number || "N/A"}</strong>
                </p>
              </div>
              <button
                className="std-modal-close"
                onClick={handleCloseCoursesModal}
                title="Close"
                style={{ color: "#ffffff" }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="std-modal-body" style={{ padding: "12px 16px", maxHeight: "75vh", overflowY: "auto" }}>
              {loadingCourses ? (
                <div style={{ textAlign: "center", padding: "30px 0" }}>
                  <Loader className="spinner" size={26} style={{ margin: "0 auto 8px" }} />
                  <div style={{ color: "#64748b", fontSize: "13px" }}>Loading student's class courses...</div>
                </div>
              ) : studentCourses.length === 0 ? (
                <div style={{ textAlign: "center", padding: "24px 16px", background: "#f8fafc", borderRadius: "10px", border: "1px dashed #cbd5e1" }}>
                  <BookOpen size={32} color="#94a3b8" style={{ margin: "0 auto 8px" }} />
                  <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#334155" }}>
                    No Courses Found for this Class
                  </h4>
                  <p style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                    No active courses assigned for Class ID ({modalStudent.class_id}) under School ID ({schoolId}).
                  </p>
                </div>
              ) : (
                <div>
                  {/* Select All Checkbox Header + Total Amount Badge */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 12px",
                      background: "#f1f5f9",
                      borderRadius: "6px",
                      marginBottom: "10px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#334155",
                    }}
                  >
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                      <input
                        type="checkbox"
                        checked={studentCourses.length > 0 && selectedCourseIds.length === studentCourses.length}
                        onChange={handleToggleSelectAll}
                        style={{ width: "15px", height: "15px", cursor: "pointer", accentColor: "#1c498d" }}
                      />
                      Select All Courses ({selectedCourseIds.length}/{studentCourses.length} Selected)
                    </label>
                    <span style={{ background: "#eff6ff", color: "#1c498d", padding: "2px 10px", borderRadius: "16px", border: "1px solid #bfdbfe", fontWeight: "700", fontSize: "12px" }}>
                      Total Amount: ₹{totalSelectedAmount.toFixed(2)}
                    </span>
                  </div>

                  {/* 2 Courses per row (Side-by-side) with Round Checkboxes */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
                    {studentCourses.map((crs) => {
                      const price = getCoursePrice(crs);
                      const origPrice = parseFloat(crs.price) || 0;
                      const discPrice = parseFloat(crs.discount_price) || price;
                      const hasDiscount = !crs.is_free && crs.discount_price != null && parseFloat(crs.discount_price) < origPrice;

                      const fullyPaid = isCourseFullyPaid(crs);
                      const totalPaid = parseFloat(crs.total_paid_amount) || 0;
                      const pendingAmt = parseFloat(crs.pending_amount) ?? Math.max(0, price - totalPaid);
                      const isPartial = crs.payment_status === "partial" || (totalPaid > 0 && totalPaid < price);

                      const isSelected = selectedCourseIds.includes(crs.id) || fullyPaid;

                      return (
                        <div
                          key={crs.id}
                          onClick={() => handleToggleCourseSelect(crs.id)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "8px 10px",
                            borderRadius: "8px",
                            border: fullyPaid
                              ? "2px solid #16a34a"
                              : isSelected
                              ? "2px solid #1c498d"
                              : "1px solid #e2e8f0",
                            background: fullyPaid
                              ? "#f0fdf4"
                              : isSelected
                              ? "#eff6ff"
                              : "#ffffff",
                            cursor: fullyPaid ? "default" : "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {/* Round Checkbox / Paid Badge */}
                          {fullyPaid ? (
                            <div
                              title="Fully Paid"
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "#16a34a",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Check size={12} color="#ffffff" strokeWidth={3} />
                            </div>
                          ) : (
                            <div
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                border: isSelected ? "2px solid #1c498d" : "2px solid #cbd5e1",
                                background: isSelected ? "#1c498d" : "#ffffff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                transition: "all 0.2s ease",
                              }}
                            >
                              {isSelected && <Check size={12} color="#ffffff" strokeWidth={3} />}
                            </div>
                          )}

                          {/* Thumbnail / Icon */}
                          <div
                            style={{
                              width: "34px",
                              height: "34px",
                              borderRadius: "6px",
                              background: "#f1f5f9",
                              overflow: "hidden",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {crs.thumbnail_url ? (
                              <img
                                src={crs.thumbnail_url}
                                alt={crs.name}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            ) : (
                              <BookOpen size={16} color="#64748b" />
                            )}
                          </div>

                          {/* Details & Payment Logs */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "12px", fontWeight: "600", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {crs.name}
                            </div>
                            
                            {/* Price / Discount Display */}
                            <div style={{ fontSize: "11px", color: "#475569", display: "flex", alignItems: "center", gap: "4px", marginTop: "1px" }}>
                              {crs.is_free ? (
                                <span style={{ color: "#166534", fontWeight: "700" }}>Free Course</span>
                              ) : hasDiscount ? (
                                <>
                                  <span style={{ textDecoration: "line-through", color: "#94a3b8" }}>₹{origPrice}</span>
                                  <span style={{ color: "#1c498d", fontWeight: "700" }}>₹{discPrice}</span>
                                </>
                              ) : (
                                <span style={{ color: "#1c498d", fontWeight: "700" }}>₹{price}</span>
                              )}
                              <span>•</span>
                              <span>{crs.subject_name || "General"}</span>
                            </div>

                            {/* Payment Logs */}
                            <div style={{ marginTop: "2px" }}>
                              {fullyPaid ? (
                                <span style={{ fontSize: "10px", fontWeight: "600", color: "#15803d", background: "#dcfce7", padding: "1px 5px", borderRadius: "3px" }}>
                                  Paid: ₹{totalPaid.toFixed(2)} {crs.payments && crs.payments.length > 1 ? `(${crs.payments.length} payments)` : ''}
                                </span>
                              ) : isPartial ? (
                                <span style={{ fontSize: "10px", fontWeight: "600", color: "#b45309", background: "#fef3c7", padding: "1px 5px", borderRadius: "3px" }}>
                                  Paid: ₹{totalPaid.toFixed(2)} | Pending: ₹{pendingAmt.toFixed(2)} {crs.payments && crs.payments.length > 1 ? `(${crs.payments.length} payments)` : ''}
                                </span>
                              ) : (
                                <span style={{ fontSize: "10px", color: "#64748b" }}>
                                  Pending: ₹{price.toFixed(2)}
                                </span>
                              )}
                            </div>

                            {/* Detailed List of Partial Payments */}
                            {Array.isArray(crs.payments) && crs.payments.length > 0 && (
                              <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "2px" }}>
                                {crs.payments.map((p, pIdx) => (
                                  <div
                                    key={p.id || pIdx}
                                    style={{
                                      fontSize: "10px",
                                      color: "#334155",
                                      background: "#f8fafc",
                                      padding: "2px 5px",
                                      borderRadius: "4px",
                                      border: "1px solid #e2e8f0",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span>
                                      Pay #{pIdx + 1}: <strong style={{ color: "#166534" }}>₹{parseFloat(p.amount).toFixed(2)}</strong> ({p.payment_method || "cash"})
                                    </span>
                                    {p.payment_date && (
                                      <span style={{ fontSize: "9px", color: "#94a3b8" }}>
                                        {new Date(p.payment_date).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Badge */}
                          <div style={{ flexShrink: 0 }}>
                            {fullyPaid ? (
                              <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "700" }}>
                                PAID
                              </span>
                            ) : isPartial ? (
                              <span style={{ background: "#fef3c7", color: "#b45309", padding: "2px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "700" }}>
                                PARTIAL
                              </span>
                            ) : crs.is_free ? (
                              <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "700" }}>
                                FREE
                              </span>
                            ) : (
                              <span style={{ background: "#f1f5f9", color: "#475569", padding: "2px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "700" }}>
                                NOT PAID
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Below Courses: Payment Details Section */}
                  <div
                    style={{
                      borderTop: "1px dashed #cbd5e1",
                      paddingTop: "8px",
                      marginTop: "4px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 12px",
                        background: "#f8fafc",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        marginBottom: "4px",
                      }}
                    >
                      {/* How much paid in disabled mode */}
                      <div>
                        <label className="std-field-label" style={{ marginBottom: "2px", fontSize: "12px" }}>
                          How Much Paid (₹)
                        </label>
                        <input
                          type="text"
                          disabled
                          readOnly
                          value={`₹${totalSelectedAmount.toFixed(2)}`}
                          className="std-field-input"
                          style={{
                            background: "#e2e8f0",
                            color: "#1c498d",
                            fontWeight: "700",
                            cursor: "not-allowed",
                            height: "32px",
                            padding: "4px 8px",
                            fontSize: "12.5px",
                          }}
                        />
                      </div>

                      {/* Payment Mode */}
                      <div>
                        <label className="std-field-label" style={{ marginBottom: "2px", fontSize: "12px" }}>
                          Payment Mode <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <select
                          value={paymentType}
                          onChange={(e) => setPaymentType(e.target.value)}
                          className="std-field-input"
                          style={{
                            height: "32px",
                            padding: "4px 8px",
                            fontSize: "12.5px",
                          }}
                        >
                          <option value="cash">Cash</option>
                          <option value="online">Online / UPI QR</option>
                          <option value="bank_transfer">Bank Transfer</option>
                        </select>
                      </div>

                      {/* Upload Screenshot (Optional) */}
                      <div style={{ gridColumn: "span 2", marginTop: "2px" }}>
                        <label className="std-field-label" style={{ marginBottom: "2px", fontSize: "12px" }}>
                          Upload Screenshot (Optional)
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setScreenshotFile(file);
                              setScreenshotPreview(URL.createObjectURL(file));
                            } else {
                              setScreenshotFile(null);
                              setScreenshotPreview(null);
                            }
                          }}
                          className="std-field-input"
                          style={{ padding: "3px 6px", height: "32px", fontSize: "12px" }}
                        />
                        {screenshotPreview && (
                          <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                            <img
                              src={screenshotPreview}
                              alt="Screenshot Preview"
                              style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                            />
                            <span style={{ fontSize: "11px", color: "#16a34a", fontWeight: "600" }}>Screenshot Attached</span>
                          </div>
                        )}
                      </div>

                      {/* Remarks (Optional) */}
                      <div style={{ gridColumn: "span 2", marginTop: "2px" }}>
                        <label className="std-field-label" style={{ marginBottom: "2px", fontSize: "12px" }}>
                          Remarks (Optional)
                        </label>
                        <textarea
                          rows={2}
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          placeholder="Enter any remarks or notes..."
                          className="std-field-input"
                          style={{ padding: "4px 8px", fontSize: "12.5px", resize: "vertical" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>


            {/* Footer / Submit Button */}
            <div className="std-modal-footer" style={{ padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", color: "#475569" }}>
                <span>Selected: <strong>{selectedCourseIds.length}</strong> of <strong>{studentCourses.length}</strong></span>
                <span>•</span>
                <span>Total Amount: <strong style={{ color: "#1c498d", fontSize: "13px" }}>₹{totalSelectedAmount.toFixed(2)}</strong></span>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  className="std-btn-cancel"
                  onClick={handleCloseCoursesModal}
                  style={{ padding: "6px 14px", fontSize: "12.5px" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="std-btn-primary"
                  onClick={handleSaveStudentCourses}
                  disabled={studentCourses.length === 0}
                  style={{ padding: "6px 16px", fontSize: "12.5px" }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
